import React, { useCallback, useEffect, useState } from "react";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import GenericCard from "@ui-components/Card/GenericCard";
import {
    GenericTable,
    GenericTableColumn,
} from "@ui-components/Table/GenericTable";
import { GenericButton } from "@ui-components/Button/GenericButton";
import EmptyItems from "@shared/components/EmptyItems";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { ReactComponent as IconChart } from "@assets/Icons/IconChart.svg";
import TargetHistoryModal from "./TargetHistoryModal";
import {
    getInternetIpMonitoringAPI,
    InternetIpMonitoringData,
    IpslaTarget,
    IpslaUplink,
} from "./requests/request";

type FetchState = "loading" | "error" | "ready";

/** Same clock format as the Alerts "Updated" label. */
const formatClockTime = (ts: number | null): string => {
    if (!ts) return "--:--:--";
    return new Date(ts).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    });
};

const formatLastRtt = (target: IpslaTarget): string => {
    if (target.lastRttMs === null || Number.isNaN(target.lastRttMs)) return "—";
    return `${target.lastRttMs.toFixed(2)} ms`;
};

/** Any lost probe is highlighted; a target with no samples has no verdict. */
const packetLossClass = (target: IpslaTarget): string => {
    if (target.lostPackets === null || !target.hasData) {
        return "text-semantic-text-secondary";
    }
    if (target.lostPackets === 0) return "text-semantic-text-secondary";
    return "text-red-600 font-semibold";
};

const targetColumns = (
    windowSeconds: number,
    onOpenHistory: (target: IpslaTarget) => void
): GenericTableColumn<IpslaTarget>[] => [
    {
        header: "TARGET IP",
        accessor: "targetIp",
        renderCell: (row) => (
            <span className="text-sm font-mono text-semantic-text-primary">
                {row.targetIp}
            </span>
        ),
    },
    {
        header: "PROVIDER",
        accessor: "targetLabel",
        renderCell: (row) => (
            <span className="text-sm text-semantic-text-secondary">
                {row.targetLabel}
            </span>
        ),
    },
    {
        header: "ASN",
        accessor: "targetAsn",
        renderCell: (row) => (
            <span className="text-sm text-semantic-text-secondary">
                {row.targetAsn || "—"}
            </span>
        ),
    },
    {
        // A count, not a percentage: the collector polls every ~30s, so a
        // 5-minute window holds ~10 checks and one miss would read as 10%.
        //
        // The window stays in the header — it is what the number means,
        // and a count without it is unreadable. Dropping "LAST " keeps it
        // from being the widest string on the page, over a column whose
        // values are single digits. It is derived, never hardcoded: the
        // backend owns the window.
        header: `PACKETS LOST (${Math.round(windowSeconds / 60)} MIN)`,
        accessor: "lostPackets",
        wrapMode: "nowrap",
        renderCell: (row) => (
            <span className={`text-sm ${packetLossClass(row)}`}>
                {row.lostPackets === null || !row.hasData
                    ? "—"
                    : row.lostPackets}
            </span>
        ),
    },
    {
        header: "LAST RTT",
        accessor: "lastRttMs",
        renderCell: (row) => (
            <span className="text-sm text-semantic-text-secondary">
                {formatLastRtt(row)}
            </span>
        ),
    },
    {
        header: "HISTORY",
        accessor: "operationId",
        renderCell: (row) => (
            <button
                onClick={() => onOpenHistory(row)}
                /* Anchor for the changelog walkthrough (CTX-509/510). Authored
                   alongside the entry that points at it, which is the rule that
                   keeps the two in step: a step whose selector no longer matches
                   falls back to a centered card and fails silently. */
                data-testid="ipsla-history-button"
                className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-8 h-8
                "
                title="Packet loss and RTT history"
            >
                <IconChart className="w-5 h-5" />
            </button>
        ),
    },
];

interface UplinkCardProps {
    uplink: IpslaUplink;
    windowSeconds: number;
    onOpenHistory: (uplink: IpslaUplink, target: IpslaTarget) => void;
}

/** Collapsible "flap" per uplink: header identifies the uplink, body lists its IPSLA targets. */
const UplinkCard: React.FC<UplinkCardProps> = ({
    uplink,
    windowSeconds,
    onOpenHistory,
}) => (
    <GenericCard
        isCollapsible={true}
        initialExpanded={true}
        /* Anchor for the changelog walkthrough (CTX-509/510), which spotlights
           the first of these — one flap explains all of them. `CardProps`
           extends `HTMLAttributes`, so this lands on the card's own root and
           the tour has a real element to measure. */
        data-testid="ipsla-uplink-card"
        headerContent={
            <span className="text-md font-semibold text-semantic-text-primary">
                {uplink.uplinkLabel}
            </span>
        }
    >
        <GenericTable
            columns={targetColumns(windowSeconds, (target) =>
                onOpenHistory(uplink, target)
            )}
            data={uplink.targets}
            rowKey={(row) => `${uplink.source}-${row.operationId}`}
            tableClassName="bg-semantic-bg-primary"
            width="100%"
            isFooterVisible={false}
        />
    </GenericCard>
);

/**
 * Internet IP Monitoring: which IPs are being probed with IPSLA per
 * backbone uplink. Packet loss is the count of lost probes in the window
 * (Cisco IP SLA: 0 = perfect). Partner-only — same gate as Alerts.
 */
const InternetIpMonitoringPage: React.FC = () => {
    const api = useAxiosAuth0();
    const [fetchState, setFetchState] = useState<FetchState>("loading");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<number | null>(null);
    const [monitoringData, setMonitoringData] =
        useState<InternetIpMonitoringData | null>(null);
    const [historyTarget, setHistoryTarget] = useState<{
        uplink: IpslaUplink;
        target: IpslaTarget;
    } | null>(null);

    const fetchMonitoringData = useCallback(async () => {
        try {
            const response = await getInternetIpMonitoringAPI(api);
            setMonitoringData(response.data.data);
            setLastUpdated(Date.now());
            setFetchState("ready");
        } catch {
            setFetchState("error");
        }
    }, [api]);

    useEffect(() => {
        fetchMonitoringData();
    }, [fetchMonitoringData]);

    // Manual refresh keeps the current content on screen; only the button spins.
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        try {
            await fetchMonitoringData();
        } finally {
            setIsRefreshing(false);
        }
    }, [fetchMonitoringData]);

    if (fetchState === "loading") {
        return (
            <div className="flex min-h-[240px] flex-1 items-center justify-center text-sm text-semantic-text-secondary">
                Loading...
            </div>
        );
    }

    if (fetchState === "error") {
        return (
            <ErrorFetchingItems text="Error fetching Internet IP monitoring data" />
        );
    }

    const uplinks = monitoringData?.uplinks ?? [];
    const windowSeconds = monitoringData?.windowSeconds ?? 300;

    return (
        <div className="flex flex-col gap-3 px-6 pb-6 pt-2">
            {/* The page title comes from the top bar (getPageTitle) — only the controls live here. */}
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-semantic-text-secondary">
                    Last RTT is the latest probe of the same{" "}
                    {Math.round(windowSeconds / 60)} min window.
                </span>
                <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-xs text-semantic-text-secondary">
                        Updated {formatClockTime(lastUpdated)}
                    </span>
                    <GenericButton
                        variant="secondary"
                        size="sm"
                        iconLeftName="refresh"
                        label="Refresh"
                        onClick={handleRefresh}
                        loading={isRefreshing}
                        aria-label="Refresh now"
                    />
                </div>
            </div>

            {monitoringData && !monitoringData.metricsAvailable && (
                <GenericCard isCollapsible={false} className="p-4">
                    <span className="text-sm text-semantic-text-secondary">
                        Metrics are temporarily unavailable — showing the
                        monitored targets without packet loss / RTT.
                    </span>
                </GenericCard>
            )}

            {uplinks.length === 0 ? (
                <EmptyItems />
            ) : (
                uplinks.map((uplink) => (
                    <UplinkCard
                        key={uplink.source}
                        uplink={uplink}
                        windowSeconds={windowSeconds}
                        onOpenHistory={(historyUplink, target) =>
                            setHistoryTarget({ uplink: historyUplink, target })
                        }
                    />
                ))
            )}

            {historyTarget && (
                <TargetHistoryModal
                    isOpen={true}
                    onClose={() => setHistoryTarget(null)}
                    uplinkLabel={historyTarget.uplink.uplinkLabel}
                    source={historyTarget.uplink.source}
                    operationId={historyTarget.target.operationId}
                    targetIp={historyTarget.target.targetIp}
                    targetLabel={historyTarget.target.targetLabel}
                />
            )}
        </div>
    );
};

export default InternetIpMonitoringPage;
